// third-party
import { createSlice } from '@reduxjs/toolkit';

// Dummy data for simulation
const dummyFunctions = [
    {
        id: 'fn_tenant_123_calculate_discount_a1b2c3d4',
        name: 'calculate_discount',
        version: '1.0.0',
        endpoint: '/functions/tenant_123/calculate_discount',
        status: 'deployed',
        description: 'Calculate product discount based on quantity and customer tier',
        parameters: [
            { name: 'price', type: 'float', required: true, description: 'Original price' },
            { name: 'quantity', type: 'int', required: true, description: 'Product quantity' },
            { name: 'customer_tier', type: 'str', required: true, description: 'Customer tier (bronze/silver/gold/platinum)' },
            { name: 'promo_code', type: 'str', required: false, description: 'Promotional code' }
        ],
        return_type: 'dict',
        config: {
            memory: '256Mi',
            timeout: 30,
            rate_limit: '1000/hour',
            cache_ttl: 300
        },
        metrics: {
            total_executions_24h: 15420,
            success_rate: 99.8,
            avg_latency_ms: 23,
            p95_latency_ms: 45,
            p99_latency_ms: 78,
            errors_24h: 31,
            last_execution: '2024-07-17T10:30:00Z'
        },
        created_at: '2024-07-15T08:00:00Z',
        updated_at: '2024-07-16T14:30:00Z',
        created_by: 'john.doe@company.com'
    },
    {
        id: 'fn_tenant_123_process_payment_e5f6g7h8',
        name: 'process_payment',
        version: '2.1.0',
        endpoint: '/functions/tenant_123/process_payment',
        status: 'deployed',
        description: 'Process payment transactions with validation and fraud detection',
        parameters: [
            { name: 'amount', type: 'float', required: true, description: 'Payment amount' },
            { name: 'currency', type: 'str', required: true, description: 'Currency code (USD, EUR, etc)' },
            { name: 'payment_method', type: 'dict', required: true, description: 'Payment method details' },
            { name: 'customer_id', type: 'str', required: true, description: 'Customer identifier' }
        ],
        return_type: 'dict',
        config: {
            memory: '512Mi',
            timeout: 60,
            rate_limit: '100/minute',
            async_enabled: true
        },
        metrics: {
            total_executions_24h: 8932,
            success_rate: 98.5,
            avg_latency_ms: 156,
            p95_latency_ms: 245,
            p99_latency_ms: 412,
            errors_24h: 134,
            last_execution: '2024-07-17T10:35:00Z'
        },
        created_at: '2024-07-10T10:00:00Z',
        updated_at: '2024-07-17T09:15:00Z',
        created_by: 'jane.smith@company.com'
    },
    {
        id: 'fn_tenant_123_analyze_sentiment_i9j0k1l2',
        name: 'analyze_sentiment',
        version: '1.2.0',
        endpoint: '/functions/tenant_123/analyze_sentiment',
        status: 'deployed',
        description: 'Analyze text sentiment using NLP models',
        parameters: [
            { name: 'text', type: 'str', required: true, description: 'Text to analyze' },
            { name: 'language', type: 'str', required: false, description: 'Language code (default: en)' },
            { name: 'detailed', type: 'bool', required: false, description: 'Return detailed analysis' }
        ],
        return_type: 'dict',
        config: {
            memory: '1Gi',
            timeout: 120,
            rate_limit: '500/hour',
            dependencies: ['nltk', 'transformers', 'torch']
        },
        metrics: {
            total_executions_24h: 5623,
            success_rate: 99.2,
            avg_latency_ms: 342,
            p95_latency_ms: 523,
            p99_latency_ms: 789,
            errors_24h: 45,
            last_execution: '2024-07-17T10:32:00Z'
        },
        created_at: '2024-07-12T13:00:00Z',
        updated_at: '2024-07-16T16:45:00Z',
        created_by: 'ai.team@company.com'
    },
    {
        id: 'fn_tenant_123_generate_report_m3n4o5p6',
        name: 'generate_report',
        version: '1.0.0',
        endpoint: '/functions/tenant_123/generate_report',
        status: 'deploying',
        description: 'Generate PDF reports from data',
        parameters: [
            { name: 'report_type', type: 'str', required: true, description: 'Type of report' },
            { name: 'data', type: 'dict', required: true, description: 'Report data' },
            { name: 'format', type: 'str', required: false, description: 'Output format (pdf/html)' }
        ],
        return_type: 'str',
        config: {
            memory: '512Mi',
            timeout: 300,
            rate_limit: '100/hour'
        },
        metrics: {
            total_executions_24h: 0,
            success_rate: 0,
            avg_latency_ms: 0,
            p95_latency_ms: 0,
            p99_latency_ms: 0,
            errors_24h: 0,
            last_execution: null
        },
        created_at: '2024-07-17T10:00:00Z',
        updated_at: '2024-07-17T10:00:00Z',
        created_by: 'john.doe@company.com'
    },
    {
        id: 'fn_tenant_123_validate_data_q7r8s9t0',
        name: 'validate_data',
        version: '1.1.0',
        endpoint: '/functions/tenant_123/validate_data',
        status: 'failed',
        description: 'Validate input data against schema',
        parameters: [
            { name: 'data', type: 'dict', required: true, description: 'Data to validate' },
            { name: 'schema', type: 'dict', required: true, description: 'Validation schema' }
        ],
        return_type: 'dict',
        config: {
            memory: '256Mi',
            timeout: 30,
            rate_limit: '2000/hour'
        },
        metrics: {
            total_executions_24h: 0,
            success_rate: 0,
            avg_latency_ms: 0,
            p95_latency_ms: 0,
            p99_latency_ms: 0,
            errors_24h: 0,
            last_execution: null
        },
        error_message: 'Failed to compile function: SyntaxError on line 15',
        created_at: '2024-07-17T09:30:00Z',
        updated_at: '2024-07-17T09:35:00Z',
        created_by: 'dev.team@company.com'
    }
];

// Dummy execution history
const dummyExecutions = [
    {
        id: 'exec_001',
        function_id: 'fn_tenant_123_calculate_discount_a1b2c3d4',
        request_id: 'req_abc123',
        status: 'success',
        execution_time_ms: 23,
        input_data: { price: 100, quantity: 10, customer_tier: 'gold' },
        output_data: { discount: 15, final_price: 85 },
        created_at: '2024-07-17T10:30:00Z'
    },
    {
        id: 'exec_002',
        function_id: 'fn_tenant_123_calculate_discount_a1b2c3d4',
        request_id: 'req_def456',
        status: 'failed',
        execution_time_ms: 12,
        input_data: { price: -50, quantity: 10, customer_tier: 'gold' },
        error_message: 'ValueError: Price cannot be negative',
        created_at: '2024-07-17T10:29:00Z'
    }
];

// Dummy test results
const dummyTestResults = {
    fn_tenant_123_calculate_discount_a1b2c3d4: {
        success: true,
        request_id: 'test_123',
        result: {
            original_price: 100,
            quantity: 5,
            discount_percentage: 10,
            discount_amount: 10,
            final_price: 90,
            savings: 10
        },
        execution_time_ms: 15,
        timestamp: '2024-07-17T10:35:00Z'
    }
};

// ==============================|| SLICE - LAMBDA FUNCTIONS ||============================== //

const lambdaFunctionsSlice = createSlice({
    name: 'lambdaFunctions',
    initialState: {
        functions: dummyFunctions,
        selectedFunction: null,
        executions: dummyExecutions,
        testResults: dummyTestResults,
        deploymentStatus: null,
        loading: false,
        error: null,
        filters: {
            status: 'all',
            search: ''
        },
        metrics: {
            totalFunctions: dummyFunctions.length,
            deployedFunctions: dummyFunctions.filter((f) => f.status === 'deployed').length,
            failedFunctions: dummyFunctions.filter((f) => f.status === 'failed').length,
            totalExecutions24h: dummyFunctions.reduce((sum, f) => sum + f.metrics.total_executions_24h, 0),
            avgSuccessRate: dummyFunctions.reduce((sum, f) => sum + f.metrics.success_rate, 0) / dummyFunctions.length
        }
    },
    reducers: {
        // Set selected function
        setSelectedFunction(state, action) {
            state.selectedFunction = action.payload;
        },

        // Update function status (simulation)
        updateFunctionStatus(state, action) {
            const { functionId, status } = action.payload;
            const func = state.functions.find((f) => f.id === functionId);
            if (func) {
                func.status = status;
                if (status === 'deployed') {
                    func.metrics.last_execution = new Date().toISOString();
                }
            }
            state.metrics.deployedFunctions = state.functions.filter((f) => f.status === 'deployed').length;
            state.metrics.failedFunctions = state.functions.filter((f) => f.status === 'failed').length;
        },

        // Add new function (simulation)
        addFunction(state, action) {
            const newFunction = {
                ...action.payload,
                id: `fn_tenant_123_${action.payload.name}_${Math.random().toString(36).substr(2, 8)}`,
                endpoint: `/functions/tenant_123/${action.payload.name}`,
                status: 'deploying',
                metrics: {
                    total_executions_24h: 0,
                    success_rate: 0,
                    avg_latency_ms: 0,
                    p95_latency_ms: 0,
                    p99_latency_ms: 0,
                    errors_24h: 0,
                    last_execution: null
                },
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            state.functions.push(newFunction);
            state.metrics.totalFunctions = state.functions.length;
        },

        // Delete function
        deleteFunction(state, action) {
            state.functions = state.functions.filter((f) => f.id !== action.payload);
            state.metrics.totalFunctions = state.functions.length;
            state.metrics.deployedFunctions = state.functions.filter((f) => f.status === 'deployed').length;
        },

        // Update test results
        updateTestResults(state, action) {
            const { functionId, result } = action.payload;
            state.testResults[functionId] = result;
        },

        // Add execution record
        addExecution(state, action) {
            state.executions.unshift(action.payload);
            if (state.executions.length > 100) {
                state.executions.pop();
            }
        },

        // Set filters
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },

        // Update function metrics (simulation)
        updateFunctionMetrics(state, action) {
            const { functionId, metrics } = action.payload;
            const func = state.functions.find((f) => f.id === functionId);
            if (func) {
                func.metrics = { ...func.metrics, ...metrics };
            }
        },

        // Set deployment status
        setDeploymentStatus(state, action) {
            state.deploymentStatus = action.payload;
        },

        // Set loading state
        setLoading(state, action) {
            state.loading = action.payload;
        },

        // Set error
        setError(state, action) {
            state.error = action.payload;
        },

        // Update function
        updateFunction(state, action) {
            const { id, ...updates } = action.payload;
            const funcIndex = state.functions.findIndex((f) => f.id === id);
            if (funcIndex !== -1) {
                state.functions[funcIndex] = {
                    ...state.functions[funcIndex],
                    ...updates
                };
            }
        },

        // Clear test results
        clearTestResults(state, action) {
            const { functionId } = action.payload;
            delete state.testResults[functionId];
        },

        // Batch update functions
        batchUpdateFunctions(state, action) {
            const updates = action.payload;
            updates.forEach((update) => {
                const funcIndex = state.functions.findIndex((f) => f.id === update.id);
                if (funcIndex !== -1) {
                    state.functions[funcIndex] = {
                        ...state.functions[funcIndex],
                        ...update
                    };
                }
            });
        }
    }
});

export const {
    setSelectedFunction,
    updateFunctionStatus,
    addFunction,
    deleteFunction,
    updateTestResults,
    addExecution,
    setFilters,
    updateFunctionMetrics,
    setDeploymentStatus,
    setLoading,
    setError,
    updateFunction,
    clearTestResults,
    batchUpdateFunctions
} = lambdaFunctionsSlice.actions;

export default lambdaFunctionsSlice.reducer;
